import { useEffect } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useDispatch } from "react-redux";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import api from "../api/api";
import useUrlQuery from "../hooks/useUrlQuery";
import {
  goNextIndex,
  resetProposal,
  setInspectionInfo,
  setProposalData,
} from "../pages/Proposal/Proposal.slice";

export const useInitiateProposalMutation = () => {
  const { get, set, remove } = useUrlQuery();
  const quote_id = get("quote_id");

  const dispatch = useDispatch();
  const navigate = useNavigate();

  const { isPending, mutate } = useMutation({
    mutationFn: (req) =>
      api.post("api/InitiateCarProposal", {
        ...req,
        QUOTE_ID: quote_id,
      }),

    onSuccess: ({ data }) => {
      const proposal_id = data?.response?.PROPOSAL_ID;
      const token = data?.response?.session_token;

      dispatch(resetProposal());
      toast.dismiss();

      remove("quote_id");

      if (token) {
        sessionStorage.setItem("session_token", token);
      }

      navigate(
        `/proposal?${set("proposal_id", proposal_id)}&session_token=${token}`,
      );
    },

    onError: (error) => {
      toast.error(
        error?.response?.data?.message ||
          error?.message ||
          "Unable to initiate proposal",
      );
    },
  });

  return {
    initiateProposal: mutate,
    isSubmitting: isPending,
  };
};

export const useProposalEnquiry = () => {
  const { get } = useUrlQuery();
  const proposal_id = get("proposal_id");
  const dispatch = useDispatch();

  const { data, isPending, refetch } = useQuery({
    queryKey: ["proposal-details", proposal_id],

    queryFn: async () => {
      const response = await api.post("api/GetProposalDetails", {
        PROPOSAL_ID: proposal_id,
      });

      return response?.data?.data;
    },

    enabled: !!proposal_id,

    staleTime: 0,
    refetchOnMount: "always",
    refetchOnWindowFocus: false,

    onSuccess: (data) => {
      const formData = data?.PROPOSAL?.FORMDATA || {};
      dispatch(setProposalData(formData));
    },
  });

  const proposal = data?.PROPOSAL || {};

  return {
    isLoading: isPending,
    proposal,
    vehicle: data?.QUOTE_REQUEST,
    product: data?.product_data,
    refetch,
  };
};

export const useUpdateProposalMutation = (isFormData) => {
  const { get } = useUrlQuery();
  const proposal_id = get("proposal_id");

  const dispatch = useDispatch();

  const mutation = useMutation({
    mutationFn: (data) => {
      const formData = new FormData();

      const newData = {
        PROPOSAL_ID: proposal_id,
        ...data,
      };

      Object.keys(newData).forEach((key) => {
        formData.append(key, newData[key]);
      });

      return api.post(
        "api/UpdateRawProposal",
        isFormData
          ? formData
          : {
              PROPOSAL_ID: proposal_id,
              FORMDATA: data,
            },
        {
          headers: {
            "Content-Type": isFormData
              ? "multipart/form-data"
              : "application/json",
          },
        },
      );
    },

    onSuccess: () => {
      dispatch(goNextIndex());
      toast.success("Details Updated Successfully!");
    },
  });

  return {
    submit: mutation.mutate,
    asyncSubmit: mutation.mutateAsync,
    isLoading: mutation.isPending,
    isSuccess: mutation.isSuccess,
  };
};

export const useGetRelationMasterQuery = (company_slug) => {
  const { data, isPending, isError, refetch } = useQuery({
    queryKey: ["relation-master", company_slug],

    queryFn: async () => {
      const res = await api.post("api/GetRelationMaster", {
        LOB: "car",
        COMPANY_SLUG: company_slug,
      });
      return res.data;
    },

    enabled: !!company_slug, // ✅ prevents API call if undefined/null
  });

  return {
    relations: data?.response || [],
    isLoading: isPending,
    isError,
    refetch,
  };
};

export const useGetOccupationMasterQuery = (company_slug) => {
  const { data, isPending, isError, error, refetch } = useQuery({
    queryKey: ["occupations", company_slug],

    queryFn: async () => {
      try {
        const res = await api.post("api/GetOccupationMaster", {
          LOB: "CAR",
          COMPANY_SLUG: company_slug,
        });

        return res.data;
      } catch (err) {
        console.log("Occupation API error:", err?.response?.data || err);
        throw err;
      }
    },

    staleTime: Infinity,
  });

  return {
    occupations: data?.response || [],
    isLoading: isPending,
    isError,
    error,
    refetch,
  };
};

export const useGetPreviousInsurerMasterQuery = (company_slug) => {
  const { data, isPending, isError, refetch } = useQuery({
    queryKey: ["previous_insurer", company_slug],

    queryFn: async () => {
      const res = await api.post("api/GetPreviousInsurerMaster", {
        LOB: "car",
        COMPANY_SLUG: company_slug,
      });
      return res.data;
    },

    enabled: !!company_slug, // ✅ prevents API call if slug undefined
    staleTime: Infinity,
  });

  return {
    previous_insurer: data?.response || [], // ✅ correct response extraction
    isLoading: isPending,
    isError,
    refetch,
  };
};

export const useGetPincodeMutation = () => {
  const mutation = useMutation({
    mutationFn: ({ company_alias, pincode }) =>
      api.post("api/GetPincodeMaster", {
        pincode,
        company_alias,
      }),

    onError: () => {
      toast.error("Wrong Pincode!!");
    },
  });

  return {
    isLoading: mutation.isPending,
    getCityState: mutation.mutate,
    city: mutation.data?.data?.response?.city,
    state: mutation.data?.data?.response?.state,
    isError: mutation.isError,
  };
};

export const serialize = function (obj) {
  var str = [];
  for (var p in obj)
    if (obj.hasOwnProperty(p)) {
      str.push(encodeURIComponent(p) + "=" + encodeURIComponent(obj[p]));
    }
  return str.join("&");
};

export const useFinalSubmitMutation = () => {
  const { get, set } = useUrlQuery();
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const proposal_id = get("proposal_id");

  const mutation = useMutation({
    mutationFn: async () => {
      try {
        if (!proposal_id) {
          throw new Error("Proposal ID is missing");
        }

        const res = await api.post("/api/ProposalFinalSubmit", {
          PROPOSAL_ID: proposal_id,
        });

        return res.data;
      } catch (error) {
        console.error(
          "Final Submit API Error:",
          error?.response?.data || error.message,
        );
        throw error;
      }
    },

    onSuccess: (data) => {
      if (data?.BREAKIN_DATA) {
        dispatch(setInspectionInfo(data.BREAKIN_DATA));
        return;
      }

      navigate(`/summary?${set("proposal_id", proposal_id)}`);
    },

    onError: (error) => {
      console.error(
        "Final Submit Failed:",
        error?.response?.data || error.message,
      );
    },
  });

  return {
    finalSubmit: mutation.mutateAsync,
    finalSubmitData: mutation.data,
    isLoading: mutation.isPending,
    status: mutation.status,
  };
};

export const usePaymentQuery = () => {
  const { get, set } = useUrlQuery();
  const navigate = useNavigate();
  const proposal_id = get("proposal_id");
  const { proposal } = useProposalEnquiry();

  const { isPending, data } = useQuery({
    queryKey: ["payment", proposal_id],

    queryFn: async () => {
      const res = await api.post("api/GetPaymentDetails", {
        PROPOSAL_ID: proposal_id,
      });
      return res;
    },

    enabled: !!proposal_id,

    onSuccess: (data) => {
      const status = data?.data?.status;
      const resubmit = data?.data?.resubmit;

      if (status === 500 && !resubmit) {
        navigate(`/proposal?${set("proposal_id", proposal_id)}`);
        return;
      }

      if (status === 200 && proposal?.COMPANY_SLUG === "shri_ram") {
        const { fields, method, url } = data.data?.PG_FORM || {};

        if (method === "POST" && fields && url) {
          const form = document.createElement("form");
          form.method = "POST";
          form.action = url;
          form.target = "_blank";

          Object.keys(fields).forEach((key) => {
            const input = document.createElement("input");
            input.type = "hidden";
            input.name = key;
            input.value = fields[key];
            form.appendChild(input);
          });

          document.body.appendChild(form);
          form.submit();
        }
      }
    },
  });

  return {
    data: data?.data,
    isPaymentLoading: isPending,
    isResubmit: data?.data?.resubmit,
  };
};

export const useFinancerMasterQuery = (company_slug) => {
  const { isPending, data, isError, refetch } = useQuery({
    queryKey: ["financer", company_slug],

    queryFn: async () => {
      if (!company_slug) return [];

      const res = await api.post("api/GetFinancierMaster", {
        LOB: "car",
        COMPANY_SLUG: company_slug,
      });

      return res.data;
    },

    enabled: !!company_slug, // prevents API call if empty
  });

  return {
    financer: data?.response || [],
    isLoading: isPending,
    isError,
    refetch,
  };
};
export const useUpdateOrderMutation = () => {
  const { get } = useUrlQuery();
  const proposal_id = get("proposal_id");

  const mutation = useMutation({
    mutationFn: () =>
      api.post("api/UpdateOrderStage", {
        PROPOSAL_ID: proposal_id,
        ORDER_STAGE: "PG REDIRECTED",
      }),
  });

  return {
    updateOrder: mutation.mutate,
    isLoading: mutation.isPending,
    isSuccess: mutation.isSuccess,
  };
};
export const useVerifyPanCKYC = () => {
  const queryClient = useQueryClient();
  const { product } = useProposalEnquiry();

  const {
    data,
    mutate,
    mutateAsync,
    isPending, // v5 uses isPending instead of isLoading
    reset,
  } = useMutation({
    mutationFn: (formData) => {
      return api.post(
        product?.INSURANCE_SLUG === "reliance"
          ? "api/GetRelianceCKYCDetails"
          : "api/GetHdfcCKYCDetails",
        { ...formData, LOB: "CAR" },
      );
    },

    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ["selected"],
      });
    },

    onError: (error) => {
      const message = error?.response?.data?.message || "Something went wrong";
      toast.error(message);
    },
  });

  return {
    verifyCKYC: mutateAsync,
    isVerifyCKYC: isPending, // renamed
    resetVerify: reset,
  };
};

export const getMobileNumber = async (PROPOSAL_ID) => {
  const res = await api.post("/api/getCarProposalOTP", {
    PROPOSAL_ID,
  });

  return res.data;
};

export const verifyOtpApi = async (PROPOSAL_ID, OTP) => {
  const res = await api.post("/api/verifyCarProposalOTP", {
    PROPOSAL_ID,
    OTP,
  });

  return res.data;
};
